import { useState, useRef } from 'react';
import { CTABanner } from '../../components/ui/UtilsLayout';
import { iconsPath, imagePath } from '../../utils/imagePath';
import {
  ImageWithFeatureList,
  IndustriesImagesCard,
  RetailECommerceFeatureCard,
} from '../../utils/IndustriesData/IndustriesData';
import {
  caseStudies,
  DefenceFeaturesData,
} from '../../components/data/data/Industries/IndustriesStaticData';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import toast from 'react-hot-toast';
import MissileSimulationPopup from '../../components/MissileSimulationPopup';
import { CalendlyScheduler } from '../../components/ScheduleDemoComponents/ScheduleDemoComponents';

export const DefenceAerospace = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showScheduler, setShowScheduler] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const touchStartX = useRef(null);
  const touchEndX = useRef(null);

  const openScheduler = () => setShowScheduler(true);
  const closeScheduler = () => setShowScheduler(false);

  const cards = [
    {
      image: imagePath.MissileSimulation,
      title: 'Missile Trajectory Simulation',
      description:
        'An immersive simulation visualizing missile trajectories through real-time physics, environment and guidance logic to support training and operational scenario evaluation.',
      onClick: () => setIsPopupOpen(true),
    },
    {
      image: imagePath.GeospatialManagement,
      title: 'Geospatial Disaster Management',
      description:
        'An immersive geospatial experience visualizing a flooding scenario through real-time data and spatial analytics to support preparedness, response planning, and decision-making.',
      to: '/all-industries/geo-spatial',
    },
    {
      image: imagePath.FourdImmersive,
      title: '4D Immersive Experience',
      description:
        'A 4D immersive space experience using VR and projection systems for stakeholder engagement and student outreach, visualizing space missions, assets, and celestial bodies.',
      to: '/all-industries/space-4dimmersive-experience',
    },
  ];

  const TOTAL = cards.length;

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % TOTAL);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + TOTAL) % TOTAL);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchEndX.current = null;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (touchStartX.current === null || touchEndX.current === null) return;
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 50) {
      diff > 0 ? nextSlide() : prevSlide();
    }
    touchStartX.current = null;
    touchEndX.current = null;
  };

  return (
    <div className="relative pt-14 sm:pt-20 md:pt-0">
      <div className="hidden lg:block absolute -top-[460px] right-[10px] md:right-[20px] lg:right-[0px]">
        <img
          className="w-[200px] md:w-[250px] lg:w-[430px] h-auto object-contain"
          src={imagePath?.reaearch1}
          alt="Grid AI"
        />
      </div>
      <div className="hidden lg:block absolute -top-[460px] right-[10px] md:right-[20px] lg:right-[0px]">
        <img
          className="w-[200px] md:w-[250px] lg:w-[430px] h-auto object-contain"
          src={imagePath?.reaearch1}
          alt="Grid AI"
        />
      </div>

      {/* Hero / Image Section */}
      <section className="relative z-10 mt-4 sm:mt-6 md:mt-24 lg:mt-10 flex items-start lg:items-center px-3 sm:px-4 md:px-6 lg:px-8 py-8 lg:py-28">
        <div className="max-w-7xl w-full mx-auto">
          <IndustriesImagesCard
            title="Defence and"
            subtitle=" Aerospace"
            description="Advancing mission readiness through realistic simulation and spatial computing."
            image={imagePath?.DefenceAerospace}
            mobileImage={imagePath?.DefenceAerospaceMobile}
            buttonText="Schedule a Demo"
            loading="eager"
            fetchpriority="high"
            onButtonClick={openScheduler}
          />
        </div>
      </section>

      <div className="hidden lg:block absolute sm:top-[320px] md:top-[320px] lg:top-[655px] left-0 z-[0]">
        <img
          className="w-[100px] sm:w-[180px] md:w-[460px] lg:w-[530px] h-auto object-contain"
          src={imagePath?.Research4}
          alt="Grid AI"
        />
      </div>

      <section className="relative z-10 w-full bg-[#1E1E22] py-8 sm:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-10">
          <div className="mb-6 sm:mb-8 md:mb-10 lg:mb-12 max-w-full sm:max-w-6xl">
            <div className="flex items-center justify-between mb-2 sm:mb-3 md:mb-4">
              <h2 className="text-[#FFFFFF] text-2xl sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl font-extrabold leading-tight">
                Our Case Studies
              </h2>
            </div>

            <p className="text-[#FFFFFF] text-md sm:text-sm md:text-base lg:text-lg leading-relaxed">
              Delivering spatial training and simulation solutions that enhance
              operational accuracy, strengthen preparedness, and support complex
              defence and space workflows.
            </p>

            {/* Mobile Arrow Navigation */}
            <div className="flex sm:hidden gap-2 mt-4 justify-end">
              <button
                onClick={prevSlide}
                className="w-8 h-8 flex items-center justify-center bg-[#31333A] hover:bg-[#3d3f47] rounded-full transition-colors"
                aria-label="Previous slide"
              >
                <ChevronLeft className="w-5 h-5 text-white" />
              </button>
              <button
                onClick={nextSlide}
                className="w-8 h-8 flex items-center justify-center bg-[#31333A] hover:bg-[#3d3f47] rounded-full transition-colors"
                aria-label="Next slide"
              >
                <ChevronRight className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>

          {/* Mobile Carousel */}
          <div
            className="sm:hidden relative overflow-hidden"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div
              className="flex transition-transform duration-300 ease-in-out"
              style={{ transform: `translateX(-${currentSlide * 100}%)` }}
            >
              {cards.map((card, index) => (
                <div key={index} className="flex-shrink-0 w-full px-1">
                  <RetailECommerceFeatureCard
                    image={card.image}
                    title={card.title}
                    description={card.description}
                    {...(card.to ? { to: card.to } : {})}
                    {...(card.onClick ? { onClick: card.onClick } : {})}
                  />
                </div>
              ))}
            </div>

           
          </div>

          {/* Desktop/Tablet Grid */}
          <div className="hidden sm:grid sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-5 lg:gap-7">
            {cards.map((card, index) => (
              <RetailECommerceFeatureCard
                key={index}
                image={card.image}
                title={card.title}
                description={card.description}
                {...(card.to ? { to: card.to } : {})}
                {...(card.onClick ? { onClick: card.onClick } : {})}
              />
            ))}
          </div>

          {isPopupOpen && (
            <MissileSimulationPopup onClose={() => setIsPopupOpen(false)} />
          )}
        </div>
      </section>

      <section className="relative w-full overflow-hidden">
        {/* Background Image */}
        <img
          src={imagePath?.Flying}
          alt="Flying"
          className="w-full aspect-[16/9] sm:aspect-auto object-contain sm:object-cover"
        />

        {/* Top Overlay */}
        <div className="absolute top-0 left-0 w-full">
          <div className="w-full bg-gradient-to-b from-[#1e1f22] via-[#1e1f22]/70 to-[#1e1f22]/0">
            <div className="max-w-6xl mx-auto px-4 pt-1 sm:pt-6 md:pt-10 flex flex-col items-center text-center">
              <img
                src={imagePath?.CommondControl}
                alt="Logo"
                className="w-14 sm:w-16 md:w-20 lg:w-24 mb-2 md:mb-3 lg:mb-4 object-contain"
              />
              <h1 className="text-white text-3xl sm:text-xl md:text-3xl lg:text-5xl font-bold mb-2 md:mb-3">
                Command and Control
              </h1>
              <p className="text-white font-anybody text-xs sm:text-sm md:text-base lg:text-lg max-w-xs sm:max-w-md md:max-w-2xl font-semibold">
                Spatial Grid Tactical & Training Platform
              </p>
            </div>
          </div>
        </div>

        {/* Bottom Text */}
        <div className="absolute bottom-6 sm:bottom-6 md:bottom-10 left-1/2 -translate-x-1/2">
          <h2 className="text-white font-normal text-base sm:text-lg md:text-2xl lg:text-3xl text-center">
            Observe. Analyze. Execute.
          </h2>
        </div>
      </section>

      {/* Image With Feature List */}
      <section className="w-full py-10 sm:py-14 md:py-16 lg:py-20 px-0 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <ImageWithFeatureList
            image={imagePath?.MissionReady}
            mobileFullImage={imagePath?.MissionReadyMobile}
            imageTitle={['Mission Ready.', ' Always.']}
            features={DefenceFeaturesData}
          />
        </div>
      </section>

      <div className="hidden md:block absolute bottom-[-80px] md:-left-40 lg:left-0">
        <img
          className="w-[180px] md:w-[260px] lg:w-[460px] h-auto object-contain rotate-180"
          src={imagePath?.research3}
          alt="Grid AI"
        />
      </div>

      <section className="w-full mx-auto px-0 sm:px-6 lg:px-8 py-12 max-w-[1350px]">
        <div className="relative sm:left-0 md:left-4">
          <CTABanner
            welcomeText="Train for the Mission. Every Time."
            description="Use high-fidelity spatial simulations to rehearse critical tasks, reduce risk, and elevate performance in complex operational environments."
            buttonText="Contact Us"
            navigateTo="/contact-us"
          />
        </div>
      </section>

      {showScheduler && <CalendlyScheduler onClose={closeScheduler} />}
    </div>
  );
};
